import { prefersReducedMotion } from './foundation.js';

const instances = new WeakMap();

export function nextAnnouncementIndex(current, count) {
  return count > 0 ? (current + 1) % count : 0;
}

export function canAutoMove(mode, autoplay, reduced) {
  return mode !== 'STATIC' && autoplay && !reduced;
}

export function mountAnnouncement(root) {
  if (instances.has(root)) return instances.get(root);
  const controller = new AbortController();
  const { signal } = controller;
  const mode = root.dataset.mode;
  const items = [...root.querySelectorAll('[data-announcement-item]')];
  const pauseButton = root.querySelector('[data-announcement-pause]');
  const status = root.querySelector('[data-announcement-status]');
  const motionMedia = matchMedia('(prefers-reduced-motion: reduce)');
  const autoplay = root.dataset.autoplay === 'true';
  const interval = Math.max(3, Math.min(12, Number(root.dataset.interval) || 5)) * 1000;
  let index = 0;
  let timer = null;
  let userPaused = !autoplay;
  let pointerPaused = false;
  let focusPaused = false;
  let editorPaused = false;

  function show(next, manual = false) {
    index = next;
    if (mode !== 'MARQUEE') items.forEach((item, position) => {
      const active = position === index;
      item.toggleAttribute('data-active', active);
      item.toggleAttribute('inert', !active);
      item.setAttribute('aria-hidden', String(!active));
    });
    if (manual && status) status.textContent = `${index + 1} / ${items.length}`;
  }

  function update() {
    if (timer) clearInterval(timer);
    timer = null;
    const canMove = canAutoMove(mode, autoplay || !userPaused, prefersReducedMotion());
    const playing = canMove && !userPaused && !pointerPaused && !focusPaused && !editorPaused && !root.hidden && document.visibilityState !== 'hidden';
    root.dataset.paused = String(!playing);
    if (pauseButton) {
      pauseButton.disabled = motionMedia.matches;
      pauseButton.setAttribute('aria-pressed', String(userPaused));
      pauseButton.setAttribute('aria-label', playing ? pauseButton.dataset.pauseLabel : pauseButton.dataset.playLabel);
    }
    if (playing && mode === 'ROTATING' && items.length > 1) timer = setInterval(() => show(nextAnnouncementIndex(index, items.length)), interval);
  }

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-announcement-close]')) {
      root.hidden = true;
      update();
      return;
    }
    if (event.target.closest('[data-announcement-pause]')) {
      userPaused = !userPaused;
      update();
      return;
    }
    if (event.target.closest('[data-announcement-next]')) show(nextAnnouncementIndex(index, items.length), true);
    if (event.target.closest('[data-announcement-prev]')) show(items.length ? (index - 1 + items.length) % items.length : 0, true);
  }, { signal });
  if (root.dataset.pauseHover === 'true') {
    root.addEventListener('pointerenter', () => { pointerPaused = true; update(); }, { signal });
    root.addEventListener('pointerleave', () => { pointerPaused = false; update(); }, { signal });
    root.addEventListener('focusin', () => { focusPaused = true; update(); }, { signal });
    root.addEventListener('focusout', (event) => { if (!root.contains(event.relatedTarget)) { focusPaused = false; update(); } }, { signal });
  }
  document.addEventListener('visibilitychange', update, { signal });
  motionMedia.addEventListener('change', update, { signal });
  show(0);
  update();
  const api = {
    selectBlock(blockId) {
      const selected = items.findIndex((item) => item.dataset.blockId === blockId);
      if (selected >= 0) show(selected, true);
      editorPaused = true;
      root.hidden = false;
      update();
    },
    selectSection() { editorPaused = true; root.hidden = false; update(); },
    deselect() { editorPaused = false; update(); },
    destroy() { if (timer) clearInterval(timer); controller.abort(); instances.delete(root); }
  };
  instances.set(root, api);
  return api;
}

function roots(scope) {
  return [...(scope.matches?.('[data-announcement-root]') ? [scope] : []), ...scope.querySelectorAll('[data-announcement-root]')];
}
if (typeof document !== 'undefined') {
  roots(document).forEach(mountAnnouncement);
  document.addEventListener('shopify:section:load', (event) => roots(event.target).forEach(mountAnnouncement));
  document.addEventListener('shopify:section:unload', (event) => roots(event.target).forEach((root) => instances.get(root)?.destroy()));
  document.addEventListener('shopify:section:select', (event) => roots(event.target).forEach((root) => instances.get(root)?.selectSection()));
  document.addEventListener('shopify:section:deselect', (event) => roots(event.target).forEach((root) => instances.get(root)?.deselect()));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-announcement-root]');
    if (root) instances.get(root)?.selectBlock(event.detail.blockId);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest('[data-announcement-root]');
    if (root) instances.get(root)?.deselect();
  });
}
